import { useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import { usePullToRefresh, PULL_THRESHOLD } from '@/features/sossial/hooks/usePullToRefresh';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Pencil, Search, Users, ChevronRight, Briefcase, Star, MapPin } from 'lucide-react';
import { useInView } from 'react-intersection-observer';
import { useAuth } from '@/contexts/AuthContext';
import { useFeedFilters } from '@/contexts/FeedFilterContext';
import { usePosts } from '@/features/social/hooks/usePosts';
import { useMyConnections } from '@/shared/hooks/useMyConnections';
import { useConnectionSuggestions } from '@/features/home/hooks/useConnectionSuggestions';
import { useMySignals } from '@/features/sossial/hooks/useMySignals';
import { PostCard } from '@/components/social/PostCard';
import { FramedAvatar } from '@/components/ui/FramedAvatar';
import { TopNavigation } from '@/components/layout/TopNavigation';
import { supabase } from '@/lib/supabase';
import { useI18n } from '@/contexts/I18nContext';
import { useToast } from '@/components/ui/use-toast';
import { getInitials } from '@/utils/avatarUtils';
import { BottomNavigation } from '@/components/layout/BottomNavigation';
import BeltIcon from '@/assets/images/icons/belt.png';
import { SingleStepWizardModal, WizardStepType } from '@/components/sossial/SingleStepWizardModal';
import { EditLocationModal } from '@/features/profile/components/EditLocationModal';
import { CtaCard } from '@/components/discovery/CtaCard';
import {
  filterSuggestionsByPill,
  type SuggestionPill,
} from '@/features/sossial/utils/connectionSuggestionFilters';

// ─── Scroll direction ─────────────────────────────────────────────────────────
function useScrollDirection() {
  const [hidden, setHidden] = useState(false);
  const lastScrollY = useRef(0);
  useEffect(() => {
    const onScroll = () => {
      const current = window.scrollY;
      setHidden(current > lastScrollY.current && current > 120);
      lastScrollY.current = current;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return hidden;
}

// ─── Pull-to-refresh ─────────────────────────────────────────────────────────
// Hook and PULL_THRESHOLD imported from @/features/sossial/hooks/usePullToRefresh

// ─── Connection Suggestions Section ──────────────────────────────────────────
const SUGGESTION_PILL_KEYS = ['recommended', 'gym', 'profession', 'mutuals', 'interests', 'location'] as const;

type MySignals = {
  main_academy_id: string | null;
  profession: string | null;
  has_interests: boolean;
  hobby_ids: number[];
  city: string | null;
  state: string | null;
  country: string | null;
} | null;

interface SuggestionsSectionProps {
  signals: MySignals;
  onSignalSaved: () => void;
}

const SuggestionsSection = ({ signals, onSignalSaved }: SuggestionsSectionProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const { t } = useI18n();
  const { data: suggestions, isLoading } = useConnectionSuggestions();
  const [wizardType, setWizardType] = useState<WizardStepType | null>(null);
  const [locationOpen, setLocationOpen] = useState(false);

  const SUGGESTION_PILLS: { key: SuggestionPill; label: string }[] = [
    { key: 'recommended', label: t('discovery.people_you_might_know') },
    { key: 'gym', label: t('discovery.gym_mates') },
    { key: 'profession', label: t('discovery.same_profession') },
    { key: 'mutuals', label: t('discovery.mutuals') },
    { key: 'interests', label: t('discovery.same_interests') },
    { key: 'location', label: t('discovery.near_to_you') },
  ];
  const [activePill, setActivePill] = useState<SuggestionPill>('recommended');
  const [sentIds, setSentIds] = useState<Set<string>>(new Set());
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set());

  const handleConnect = async (userId: string) => {
    if (!user) return;
    setPendingIds(prev => new Set(prev).add(userId));
    try {
      const { error } = await supabase
        .from('connections')
        .insert({ requester: user.id, recipient: userId, status: 'pending' });
      if (error) throw error;
      setSentIds(prev => new Set(prev).add(userId));
      toast({ title: t('connection.request_sent'), description: t('connection.request_sent_desc') });
    } catch {
      toast({ title: t('connection.request_error'), variant: 'destructive' });
    } finally {
      setPendingIds(prev => { const s = new Set(prev); s.delete(userId); return s; });
    }
  };

  const myAcademyId = signals?.main_academy_id;
  const allAvailable = (suggestions ?? []).filter(s => !sentIds.has(s.id));
  const discoverySignals = signals
    ? {
        main_academy_id: signals.main_academy_id,
        profession: signals.profession,
        has_interests: signals.has_interests,
        hobby_ids: signals.hobby_ids ?? [],
        city: signals.city,
        state: signals.state,
        country: signals.country,
      }
    : null;
  const visible = filterSuggestionsByPill(allAvailable, activePill, discoverySignals);

  const emptyMessageKey = (() => {
    if (activePill === 'gym') return 'discovery.no_gym_mates';
    if (activePill === 'profession') return 'discovery.no_profession_matches';
    if (activePill === 'mutuals') return 'discovery.no_mutual_matches';
    if (activePill === 'interests') return 'discovery.no_interest_matches';
    if (activePill === 'location') return 'discovery.no_location_matches';
    return 'sossial.no_suggestions_category';
  })();

  // CTA to show when a required signal is missing for the active pill
  const pillCta = (() => {
    if (activePill === 'gym' && !myAcademyId) return {
      title: t('discovery.cta.add_academy_title'),
      description: t('discovery.cta.add_academy_desc'),
      action: t('discovery.cta.add_now'),
      icon: <Users className="w-4 h-4" />,
      onClick: () => setWizardType('academy'),
    };
    if (activePill === 'profession' && !signals?.profession) return {
      title: t('discovery.cta.add_profession_title'),
      description: t('discovery.cta.add_profession_desc'),
      action: t('discovery.cta.add_now'),
      icon: <Briefcase className="w-4 h-4" />,
      onClick: () => setWizardType('profession'),
    };
    if (activePill === 'interests' && !signals?.has_interests) return {
      title: t('discovery.cta.pick_interests_title'),
      description: t('discovery.cta.pick_interests_desc'),
      action: t('discovery.cta.pick_now'),
      icon: <Star className="w-4 h-4" />,
      onClick: () => setWizardType('interests'),
    };
    if (activePill === 'location' && !signals?.country) return {
      title: t('discovery.cta.add_location_title'),
      description: t('discovery.cta.add_location_desc'),
      action: t('discovery.cta.add_now'),
      icon: <MapPin className="w-4 h-4" />,
      onClick: () => setLocationOpen(true),
    };
    return null;
  })();

  const handleLocationSave = async (data: { country: string; state: string; city: string }) => {
    if (!user) return;
    const { error } = await supabase
      .from('profiles')
      .update({ country: data.country, state: data.state, city: data.city })
      .eq('id', user.id);
    if (error) throw error;
    setLocationOpen(false);
    onSignalSaved();
  };

  if (isLoading) {
    return (
      <div className="mb-1">
        <div className="flex items-center justify-between px-4 mb-3">
          <span className="flex items-center gap-1.5 text-[13px] font-semibold text-white">
            <span className="w-1.5 h-1.5 rounded-full bg-sossiety-red inline-block" />
            {t('sossial.connect_community')}
          </span>
        </div>
        <div className="flex gap-2 px-4 mb-3 overflow-x-auto scrollbar-hide">
          {SUGGESTION_PILL_KEYS.map(k => (
            <div key={k} className="h-7 w-24 rounded-full bg-[#231a1a] animate-pulse flex-shrink-0" />
          ))}
        </div>
        <div className="flex gap-3 px-4 overflow-x-auto scrollbar-hide pb-1">
          {[1, 2, 3].map(i => (
            <div key={i} className="flex-shrink-0 w-36 h-44 rounded-xl bg-[#231a1a] animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mb-1">
      {/* Header */}
      <div className="flex items-center justify-between px-4 mb-3">
        <span className="flex items-center gap-1.5 text-[13px] font-semibold text-white">
          <span className="w-1.5 h-1.5 rounded-full bg-sossiety-red inline-block" />
          {t('sossial.connect_community')}
        </span>
        <button
          onClick={() => navigate('/sossial/connections')}
          className="flex items-center gap-0.5 text-[12px] text-sossiety-red font-medium"
        >
          {t('discovery.see_all')} <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Filter pills */}
      <div className="flex gap-2 px-4 mb-3 overflow-x-auto scrollbar-hide">
        {SUGGESTION_PILLS.map(pill => (
          <button
            key={pill.key}
            onClick={() => setActivePill(pill.key)}
            className={`flex-shrink-0 flex items-center gap-1.5 h-7 px-3 rounded-full text-[12px] font-medium transition-colors ${
              activePill === pill.key
                ? 'border border-sossiety-red bg-sossiety-red/[0.08] text-sossiety-red'
                : 'border border-white/10 text-white/70 hover:border-white/20'
            }`}
          >
            {pill.label}
          </button>
        ))}
      </div>

      {/* User cards or CTA */}
      {pillCta ? (
        <div className="px-4">
          <CtaCard {...pillCta} />
        </div>
      ) : visible.length === 0 ? (
        <div className="px-4 py-3 text-[13px] text-white/40">
          {t(emptyMessageKey)}
        </div>
      ) : (
        <div className="flex gap-3 px-4 overflow-x-auto scrollbar-hide pb-2">
          {visible.slice(0, 8).map(suggestion => (
            <div
              key={suggestion.id}
              className="flex-shrink-0 w-36 bg-[#231a1a] rounded-xl p-3 flex flex-col items-center gap-2"
            >
              <button onClick={() => navigate(`/profile/${suggestion.username}`)}>
                <FramedAvatar
                  src={suggestion.profile_picture_url}
                  initials={getInitials(suggestion.full_name)}
                  beltRank={suggestion.belt_rank || 'white'}
                  size={28}
                  frameSize={{ w: 44, h: 48 }}
                  username={suggestion.username}
                />
              </button>
              <div className="text-center w-full">
                <p className="text-[12px] font-semibold text-white leading-tight truncate">
                  {suggestion.full_name}
                </p>
                {suggestion.academy_name && (
                  <p className="text-[10px] text-white/50 truncate mt-0.5">
                    {suggestion.academy_name}
                  </p>
                )}
              </div>
              <button
                onClick={() => handleConnect(suggestion.id)}
                disabled={pendingIds.has(suggestion.id)}
                className="w-full py-1.5 rounded-full text-[11px] font-semibold bg-sossiety-red text-white hover:bg-sossiety-red-hover transition-colors disabled:opacity-50"
              >
                {pendingIds.has(suggestion.id) ? '...' : t('discovery.connect')}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Signal wizards */}
      <SingleStepWizardModal
        open={wizardType !== null}
        type={wizardType}
        onClose={() => setWizardType(null)}
        onSaved={() => { setWizardType(null); onSignalSaved(); }}
      />
      <EditLocationModal
        open={locationOpen}
        onClose={() => setLocationOpen(false)}
        onSave={handleLocationSave}
        initialData={{ country: '', state: '', city: '' }}
      />
    </div>
  );
};

// ─── Feed audience tab keys (labels resolved with t() inside component) ───────
const FEED_TAB_KEYS = ['public', 'connections', 'academy'] as const;

// ─── Main page ────────────────────────────────────────────────────────────────
const Sossial = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const fabHidden = useScrollDirection();
  const { filters, updateFilters } = useFeedFilters();
  const { activeTab } = filters;

  const [deletedPostIds, setDeletedPostIds] = useState<string[]>([]);
  const [signalsRefreshKey, setSignalsRefreshKey] = useState(0);
  const { signals } = useMySignals(user?.id, signalsRefreshKey);
  const myAcademyId = signals?.main_academy_id;
  const connectionsQuery = useMyConnections();
  const connections = connectionsQuery.data?.pages?.flat() || [];
  const connectionsLoading = connectionsQuery.isLoading;
  const myId = user?.id;

  const { ref, inView } = useInView({ rootMargin: '100px', threshold: 0 });

  const postsQuery = usePosts({ extraKey: [activeTab, myAcademyId] });
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError } = postsQuery;

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const previousTabRef = useRef(activeTab);
  useLayoutEffect(() => {
    if (previousTabRef.current !== activeTab) {
      window.scrollTo(0, 0);
      previousTabRef.current = activeTab;
    }
  }, [activeTab]);

  const handleRefresh = useCallback(async () => {
    setSignalsRefreshKey(k => k + 1);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['connection-suggestions'] }),
      queryClient.invalidateQueries({ queryKey: ['posts'] }),
    ]);
  }, [queryClient]);

  const { pullY, refreshing } = usePullToRefresh(handleRefresh);

  const connectionIds = connections.map((c: any) => c.id);

  function filterPosts(posts: any[]) {
    if (activeTab === 'public') return posts.filter(p => p.visibility === 'public');
    if (activeTab === 'connections') {
      if (connectionsLoading) return [];
      return posts.filter(p =>
        p.visibility === 'connections' &&
        (connectionIds.includes(p.user_id) || String(p.user_id) === String(myId))
      );
    }
    if (activeTab === 'academy') {
      return posts.filter(p => {
        if (p.visibility !== 'gym') return false;
        if (String(p.user_id) === String(myId)) return true;
        return myAcademyId && p.author.main_academy_id &&
          String(p.author.main_academy_id) === String(myAcademyId);
      });
    }
    return posts;
  }

  function isFeedEmpty() {
    if (!data?.pages?.length) return true;
    return !data.pages.some(page =>
      filterPosts(page).some(p => !deletedPostIds.includes(p.id))
    );
  }

  return (
    <div className="min-h-screen bg-[#160C0C]">
      <TopNavigation />

      {/* ── Pull-to-refresh indicator ── */}
      <div
        className="fixed top-16 left-0 right-0 z-30 flex justify-center pointer-events-none"
        style={{
          transform: `translateY(${Math.max(0, pullY - 16)}px)`,
          opacity: pullY > 10 ? Math.min(1, pullY / PULL_THRESHOLD) : 0,
          transition: pullY === 0 ? 'all 300ms ease' : 'none',
        }}
      >
        <div className={`w-8 h-8 rounded-full bg-[#d72e16] flex items-center justify-center shadow-lg ${refreshing ? 'animate-spin' : ''}`}>
          <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M1 4v6h6M23 20v-6h-6" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4-4.64 4.36A9 9 0 0 1 3.51 15" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </div>

      <div
        className="pt-16 pb-28"
        style={{
          transform: pullY > 0 ? `translateY(${pullY * 0.4}px)` : 'translateY(0)',
          transition: pullY === 0 ? 'transform 300ms ease' : 'none',
        }}
      >
        {/* Search bar */}
        <div className="px-4 py-3">
          <button
            type="button"
            role="search"
            aria-label={t('sossial.search_placeholder')}
            data-testid="sossial-search-trigger"
            onClick={() => navigate('/search')}
            className="w-full flex items-center gap-2.5 h-10 px-4 rounded-full bg-sossiety-input border border-white/5 text-xs text-white/40"
          >
            <Search className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
            <span>{t('sossial.search_placeholder')}</span>
          </button>
        </div>

        {/* Connection suggestions with pills */}
        <SuggestionsSection
          signals={signals}
          onSignalSaved={() => {
            setSignalsRefreshKey(k => k + 1);
            queryClient.invalidateQueries({ queryKey: ['connection-suggestions'] });
          }}
        />

        {/* Feed section header */}
        <div className="px-4 pt-4 pb-3">
          <span className="flex items-center gap-1.5 text-[13px] font-semibold text-white">
            <span className="w-1.5 h-1.5 rounded-full bg-sossiety-red inline-block" />
            {t('sossial.see_what_community')}
          </span>
        </div>

        {/* Audience tabs — pill style */}
        <div className="flex gap-2 px-4 mb-4 overflow-x-auto scrollbar-hide">
          {FEED_TAB_KEYS.map(key => (
            <button
              key={key}
              onClick={() => { if (activeTab !== key) updateFilters({ activeTab: key }); }}
              className={`flex-shrink-0 h-8 px-4 rounded-full text-[13px] font-medium transition-colors ${
                activeTab === key
                  ? 'border border-sossiety-red bg-sossiety-red/[0.08] text-sossiety-red'
                  : 'border border-white/10 text-white/70 hover:border-white/20'
              }`}
            >
              {key === 'public' ? t('social.whole_community')
                : key === 'connections' ? t('social.my_connections')
                : t('social.my_academy')}
            </button>
          ))}
        </div>

        {/* Posts */}
        <div className="space-y-0 px-4">
          {isLoading && (
            <div className="flex justify-center py-12">
              <div className="text-white/60 text-sm">{t('loading.feed')}</div>
            </div>
          )}

          {isError && (
            <div className="text-center text-red-400 py-4 text-sm">
              {t('sossial.failed_load_posts')}
            </div>
          )}

          {!isLoading && !isError && data?.pages.map((page, pageIdx) =>
            filterPosts(page)
              .filter(post => !deletedPostIds.includes(post.id))
              .map((post, idx, arr) => (
                <div key={post.id}>
                  <PostCard post={post} onDelete={id => setDeletedPostIds(p => [...p, id])} />
                  {(idx < arr.length - 1 || pageIdx < (data.pages.length - 1)) && (
                    <div className="relative flex items-center my-2">
                      <div className="flex-grow border-t border-[#332233]" />
                      <img src={BeltIcon} alt="" className="h-5 w-7 mx-2" />
                      <div className="flex-grow border-t border-[#332233]" />
                    </div>
                  )}
                </div>
              ))
          )}

          <div ref={ref} className="h-10 flex items-center justify-center">
            {isFetchingNextPage && (
              <span className="text-white/40 text-sm">{t('sossial.loading_more')}</span>
            )}
          </div>

          {!isLoading && !isError && isFeedEmpty() && (
            <div className="flex flex-col items-center justify-center min-h-[35vh] text-center">
              <div className="w-16 h-16 bg-[#281B1B] rounded-full flex items-center justify-center mb-4">
                <Users className="w-8 h-8 text-white/30" />
              </div>
              <h3 className="text-base font-semibold text-white mb-2">
                {activeTab === 'connections'
                  ? t('social.empty_connections.title')
                  : t('social.empty_posts.title')}
              </h3>
              <p className="text-white/50 text-sm max-w-xs leading-relaxed">
                {activeTab === 'connections'
                  ? t('social.empty_connections.subtitle')
                  : t('social.empty_posts.subtitle')}
              </p>
            </div>
          )}
        </div>
      </div>

      <BottomNavigation />

      {/* Floating FAB — above bottom nav, z above everything */}
      <button
        onClick={() => navigate('/sossial/create')}
        aria-label="Create post"
        className="fixed right-4 z-[60] w-14 h-14 rounded-full bg-sossiety-red flex items-center justify-center shadow-[0px_6px_16px_rgba(215,46,22,0.5)] transition-transform duration-200 ease-out active:scale-90"
        style={{
          bottom: 'calc(80px + env(safe-area-inset-bottom, 0px))',
          transform: fabHidden ? 'translateY(100px)' : 'translateY(0)',
        }}
      >
        <Pencil className="w-6 h-6 text-white" />
      </button>
    </div>
  );
};

export default Sossial;
